// anchor: { x, y } (뷰포트 좌표). 주어지면 그 지점(그린 annotation) 옆에 프롬프트를 띄운다.
export const createCustomPrompt = (message, anchor) => {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    // annotation 이 가려지지 않도록 어둡게 덮지 않고 투명하게(클릭만 차단).
    overlay.style.backgroundColor = 'transparent';
    overlay.style.zIndex = '99999';

    const modal = document.createElement('div');
    modal.style.backgroundColor = '#fff';
    modal.style.padding = '12px';
    modal.style.borderRadius = '8px';
    modal.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
    modal.style.width = '480px';
    modal.style.maxWidth = '90%';
    modal.style.fontFamily = 'sans-serif';

    const textDiv = document.createElement('div');
    textDiv.style.marginBottom = '16px';
    textDiv.style.fontSize = '15px';
    textDiv.style.color = '#333';
    textDiv.style.fontWeight = '500';
    textDiv.style.whiteSpace = 'pre-wrap';
    textDiv.innerText = message;

    const input = document.createElement('input');
    input.type = 'text';
    input.style.width = '100%';
    input.style.padding = '8px 12px';
    input.style.marginBottom = '12px';
    input.style.border = '1px solid #d9d9d9';
    input.style.borderRadius = '6px';
    input.style.boxSizing = 'border-box';
    input.style.fontSize = '14px';
    input.style.outline = 'none';

    input.addEventListener('focus', () => input.style.borderColor = '#1890ff');
    input.addEventListener('blur', () => input.style.borderColor = '#d9d9d9');

    const btnContainer = document.createElement('div');
    btnContainer.style.display = 'flex';
    btnContainer.style.justifyContent = 'flex-end';
    btnContainer.style.gap = '8px';

    const cancelBtn = document.createElement('button');
    cancelBtn.innerText = '취소';
    cancelBtn.style.padding = '6px 16px';
    cancelBtn.style.border = '1px solid #d9d9d9';
    cancelBtn.style.backgroundColor = '#fff';
    cancelBtn.style.borderRadius = '6px';
    cancelBtn.style.cursor = 'pointer';
    cancelBtn.style.fontSize = '14px';
    cancelBtn.addEventListener('mouseover', () => cancelBtn.style.color = '#1890ff');
    cancelBtn.addEventListener('mouseout', () => cancelBtn.style.color = '#333');

    const okBtn = document.createElement('button');
    okBtn.innerText = '확인';
    okBtn.style.padding = '6px 16px';
    okBtn.style.border = 'none';
    okBtn.style.backgroundColor = '#1890ff';
    okBtn.style.color = '#fff';
    okBtn.style.borderRadius = '6px';
    okBtn.style.cursor = 'pointer';
    okBtn.style.fontSize = '14px';
    okBtn.addEventListener('mouseover', () => okBtn.style.backgroundColor = '#40a9ff');
    okBtn.addEventListener('mouseout', () => okBtn.style.backgroundColor = '#1890ff');

    const closeModal = (val) => {
      document.body.removeChild(overlay);
      resolve(val);
    };

    cancelBtn.onclick = () => closeModal(null);
    okBtn.onclick = () => closeModal(input.value);

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') closeModal(input.value);
      if (e.key === 'Escape') closeModal(null);
    });

    btnContainer.appendChild(cancelBtn);
    btnContainer.appendChild(okBtn);
    modal.appendChild(textDiv);
    modal.appendChild(input);
    modal.appendChild(btnContainer);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // anchor 를 회피 영역(뷰포트 좌표 박스)으로 정규화.
    // - { left, top, right, bottom } : annotation 바운딩 박스 → 그리는 방향과 무관하게 이 박스를 피한다.
    // - { x, y } : 하위호환(점) → 크기 0 박스로 취급.
    const toAvoidRect = (a) => {
      if (!a) return null;
      if (typeof a.right === 'number' && typeof a.left === 'number' &&
          typeof a.top === 'number' && typeof a.bottom === 'number') {
        return { left: a.left, top: a.top, right: a.right, bottom: a.bottom };
      }
      if (typeof a.x === 'number' && typeof a.y === 'number') {
        return { left: a.x, top: a.y, right: a.x, bottom: a.y };
      }
      return null;
    };

    // 위치 결정: 회피 박스가 있으면 그 박스를 가리지 않게(오른쪽→왼쪽→아래→위 순) 배치, 없으면 중앙.
    const avoid = toAvoidRect(anchor);
    if (avoid) {
      const gap = 16;      // annotation 과 프롬프트 사이 간격
      const margin = 8;    // 화면 가장자리 여백
      const mw = modal.offsetWidth;
      const mh = modal.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const vCenter = (avoid.top + avoid.bottom) / 2;
      const hCenter = (avoid.left + avoid.right) / 2;
      const clampTop = (t) => Math.max(margin, Math.min(t, vh - mh - margin));
      const clampLeft = (l) => Math.max(margin, Math.min(l, vw - mw - margin));

      let left;
      let top;
      if (avoid.right + gap + mw <= vw - margin) {
        // 오른쪽
        left = avoid.right + gap;
        top = clampTop(vCenter - mh / 2);
      } else if (avoid.left - gap - mw >= margin) {
        // 왼쪽
        left = avoid.left - gap - mw;
        top = clampTop(vCenter - mh / 2);
      } else if (avoid.bottom + gap + mh <= vh - margin) {
        // 아래
        top = avoid.bottom + gap;
        left = clampLeft(hCenter - mw / 2);
      } else if (avoid.top - gap - mh >= margin) {
        // 위
        top = avoid.top - gap - mh;
        left = clampLeft(hCenter - mw / 2);
      } else {
        // 어느 쪽도 여유가 없으면 뷰포트 안으로 best-effort clamp
        left = clampLeft(avoid.right + gap);
        top = clampTop(vCenter - mh / 2);
      }

      modal.style.position = 'fixed';
      modal.style.left = `${left}px`;
      modal.style.top = `${top}px`;
    } else {
      overlay.style.display = 'flex';
      overlay.style.justifyContent = 'center';
      overlay.style.alignItems = 'center';
    }

    input.focus();
  });
};